import { isBaseBlueprint } from '@runloop/reflex-contract';
import type { Blueprint } from '@/lib/types';
import { findRepoBlueprint } from './findRepoBlueprint';

/**
 * The blueprint name a launch submits, given what the user picked and what the
 * picker offers. Shared by the launch dialog and the inline launch form so the
 * two cannot drift.
 *
 * A hand-picked blueprint wins whenever the list can still offer it, including
 * when it was built for a different repo than the one now attached: the picker
 * ranks by repo rather than scoping to it, so the pick stays reachable and an
 * explicit choice outranks the inferred default. `BlueprintRepoMismatchHint`
 * flags that case rather than this silently undoing it. A pick the list cannot
 * offer is dropped, since launching on a name the org has no build for would
 * fail.
 */
export function resolveLaunchBlueprintName(
  manualBlueprintName: string | null | undefined,
  availableBlueprints: Blueprint[],
  repoSlug: string,
): string | null {
  if (manualBlueprintName && availableBlueprints.some((bp) => bp.name === manualBlueprintName)) {
    return manualBlueprintName;
  }
  const matchingBlueprint = findRepoBlueprint(availableBlueprints, repoSlug);
  if (matchingBlueprint) return matchingBlueprint.name;
  // Prefer the org's own `base`; fall back to any base blueprint (a vanilla
  // org only sees the shared public base, whose real name is the long Runloop
  // name). Submit its real `name` so Runloop resolves the published image —
  // the display rename to `base` never reaches the launch payload.
  const base =
    availableBlueprints.find((bp) => bp.name === 'base') ??
    availableBlueprints.find((bp) => isBaseBlueprint(bp));
  return base?.name ?? null;
}
